import { NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  signal
} from '@angular/core';
import { ThemeAssetComponent } from '@partners/themes';
import { filter, take } from 'rxjs/operators';
import { DIALOG_ANIMATION } from '../../../../animations/dialog.animation';
import { TDialogButtons } from '../../../model';
import { DialogRef } from '../../services';
import { DIALOG_CONFIG } from '../../tokens';

import { DialogBodyDirective, DialogFooterDirective, DialogIconDirective, DialogTitleDirective } from '../dialog-slots';

@Component({
  standalone: true,
  selector: 'dialog-container',
  imports: [NgIf, NgStyle, NgTemplateOutlet, ThemeAssetComponent],
  template: `
    <ng-container *ngIf="hasCloseButton">
      <div class="dialog-close">
        <button tabindex="-1" mat-icon-button type="button" aria-label="סגירת חלונית" (click)="dismissDialog()">
          <theme-asset asset="close-icon" class="theme-contrast" [loadViaHttp]="true"></theme-asset>
        </button>
      </div>
    </ng-container>
    <ng-container *ngIf="iconTemplate && hasIcon">
      <div class="dialog-icon">
        <ng-container *ngTemplateOutlet="iconTemplate.tpl; context: { $implicit: dialogConfig }"></ng-container>
      </div>
    </ng-container>

    <ng-container *ngIf="titleTemplate">
      <div class="dialog-title">
        <h3>
          <ng-container *ngTemplateOutlet="titleTemplate.tpl; context: { $implicit: dialogConfig }"></ng-container>
        </h3>
      </div>
    </ng-container>

    <ng-container *ngIf="bodyTemplate">
      <div [ngStyle]="{ 'overflow-y': preventScroll() ? 'hidden' : 'auto' }" class="dialog-body">
        <ng-container *ngTemplateOutlet="bodyTemplate.tpl; context: { $implicit: dialogConfig }"></ng-container>
      </div>
    </ng-container>

    <ng-container *ngIf="footerTemplate">
      <div class="dialog-footer">
        <div class="dialog-actions">
          <ng-container *ngTemplateOutlet="footerTemplate.tpl; context: { $implicit: dialogConfig }"></ng-container>
        </div>
      </div>
    </ng-container>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [DIALOG_ANIMATION],
  host: {
    '[@dialog]': 'state',
    '(@dialog.start)': 'animationBegin($event)',
    '(@dialog.done)': 'animationComplete($event)',
    class: 'dialog-container'
  }
})
export class DialogContainerComponent implements OnInit, OnDestroy, AfterViewInit {
  state: 'open' | 'close' = 'close';

  @ContentChild(DialogIconDirective, { static: true })
  iconTemplate: DialogIconDirective;

  @ContentChild(DialogTitleDirective, { static: true })
  titleTemplate: DialogTitleDirective;

  @ContentChild(DialogBodyDirective, { static: true })
  bodyTemplate: DialogBodyDirective;

  @ContentChild(DialogFooterDirective, { static: true })
  footerTemplate: DialogFooterDirective;

  iconName = signal<string | undefined>(undefined);

  get buttons(): TDialogButtons {
    return this.dialogConfig.buttons;
  }

  get hasCloseButton(): boolean {
    return !this.dialogConfig.hideCloseIcon;
  }

  get hasIcon(): boolean {
    return !this.dialogConfig.hideIcon;
  }

  preventScroll = input<boolean>(false);

  dialogRef = inject(DialogRef);
  dialogConfig = inject(DIALOG_CONFIG);

  // private readonly _elementRef = inject(ElementRef);
  private readonly _changeDetector = inject(ChangeDetectorRef);
  private readonly _actionResponse = signal<unknown>(undefined);
  // private _animationBuilder = inject(AnimationBuilder);
  // private _animationPlayer: AnimationPlayer | undefined;

  constructor() {}

  dismissDialog<R>(result: R = undefined) {
    this.state = 'close';
    this._actionResponse.set(result);
  }

  ngOnInit(): void {
    this.state = 'open';
    this.dialogRef.backdropClick$
      .pipe(
        take(1),
        filter(() => {
          return !this.dialogConfig.isModal;
        })
      )
      .subscribe(() => {
        this.dismissDialog(false);
        this._actionResponse.set(false);
        this._changeDetector.markForCheck();
      });

    if (this.dialogConfig?.icon) {
      this.iconName.set(this.dialogConfig?.icon);
    }
  }

  ngOnDestroy(): void {
    this.state = 'close';
  }

  ngAfterViewInit(): void {
    this._changeDetector.detectChanges();
    // this.playAnimation();
  }

  // playAnimation(): void {
  //   const player = this.getAnimationPlayer();
  //   if (!player) {
  //     return;
  //   }
  //   player.play();
  // }

  animationBegin() {}

  animationComplete() {
    if (this.state === 'open') {
      return;
    }

    const response = this._actionResponse();
    this.dialogRef.dismiss(response);
  }

  // private getAnimationPlayer(): AnimationPlayer | undefined {
  //   if (!this._elementRef?.nativeElement) {
  //     return undefined;
  //   }
  //   if (!this._animationPlayer) {
  //     const factory = this._animationBuilder.build([
  //       transition('* => open', [
  //         style({ opacity: 0, transform: 'translateY(50vh) scale(.7)' }),
  //         animate('950ms cubic-bezier(0,.74,0,1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
  //       ]),
  //       transition('* => close', [
  //         style({ opacity: 1, transform: 'translateY(0%) scale(1)' }),
  //         animate('450ms cubic-bezier(.54,.76,.51,1.03)', style({ opacity: 0, transform: 'translateY(10%) scale(.7)' }))
  //       ])
  //     ]);
  //     this._animationPlayer = factory.create(this._elementRef?.nativeElement);
  //   }
  //   return this._animationPlayer;
  // }
}
